import { useLocation, useMatch, useParams } from 'react-router-dom';
import { useNavigateParams } from '@/utils/use-navigate-params';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { useCurrentPostView } from '@/components/post-planning/filters/PostsFiltersProvider';

type NavigateToPostOptions = {
    postId?: string;
    publicView?: boolean;
    publicShareId?: string;
    newPostOptions?: {
        state?: { date?: string };
    };
    ignorePostOpenCheck?: boolean; // Option to ignore the check for an already open post, useful when we know we're navigating to a new post
};

/**
 * Custom hook for post navigation.
 * Provides utilities to navigate to posts and back from posts,
 * handling both public and private views, and checking if a post is already open.
 */
export const usePostNavigation = () => {
    const { t } = useTranslation();
    const { navigate } = useNavigateParams();
    const { pathname } = useLocation();
    const { currentPostView } = useCurrentPostView();
    const { id: publicShareIdFromParams } = useParams<{ id?: string }>();
    const isShareOpen = useMatch('/share/posts/:id/:postId');
    const isPostOpen = useMatch('/posts/:view/post/:id?') || isShareOpen;

    /**
     * Generate URL to navigate to a specific post.
     * Automatically determines the correct route based on current view (list/calendar) and public/private context.
     *
     * @param options - Navigation options
     * @param options.postId - The ID of the post to navigate to
     * @param options.publicView - Whether this is a public share view
     * @param options.publicShareId - The public share ID (falls back to URL params if not provided)
     */
    const navigateToPostPath = (options?: NavigateToPostOptions) => {
        const { publicView, publicShareId = publicShareIdFromParams } = options || {};

        const normalizedPostId = options?.postId?.trim() ? `/${options.postId.trim()}` : '';

        if (publicView) {
            return `/share/posts/${publicShareId}${normalizedPostId}`;
        }

        if (currentPostView?.id) {
            return `/posts/${currentPostView.id}/post${normalizedPostId}`;
        }

        if (pathname.includes('/posts/list')) {
            return `/posts/list/post${normalizedPostId}`;
        }

        if (pathname.includes('/posts/kanban')) {
            return `/posts/kanban/post${normalizedPostId}`;
        }

        return `/posts/calendar/post${normalizedPostId}`;
    };

    /**
     * Navigate to a specific post.
     * Automatically determines the correct route based on current view (list/calendar) and public/private context.
     * Shows a snackbar if a post is already open.
     *
     * @param options - Navigation options
     * @param options.postId - The ID of the post to navigate to
     * @param options.publicView - Whether this is a public share view
     * @param options.publicShareId - The public share ID (falls back to URL params if not provided)
     */
    const navigateToPost = (options?: NavigateToPostOptions) => {
        if (!options?.ignorePostOpenCheck && isPostOpen) {
            enqueueSnackbar(t('planning.calendar.post-already-open'));
            return;
        }

        const postUrl = navigateToPostPath(options);
        if (postUrl) {
            navigate(postUrl, options?.newPostOptions);
        }
    };

    /**
     * Generate path to navigate back from a post to the posts view.
     * Automatically determines the correct route based on current view (list/calendar) and public/private context.
     *
     * @param options - Navigation options
     * @param options.publicView - Whether this is a public share view
     * @param options.publicShareId - The public share ID (falls back to URL params if not provided)
     */
    const navigateBackFromPostPath = (options?: NavigateToPostOptions) => {
        const { publicView, publicShareId = publicShareIdFromParams } = options || {};

        if (publicView) {
            return `/share/posts/${publicShareId}`;
        } else if (currentPostView?.id) {
            return `/posts/${currentPostView.id}`;
        } else if (pathname.includes('/posts/kanban')) {
            return '/posts/kanban';
        } else if (pathname.includes('/posts/list')) {
            return '/posts/list';
        } else {
            return '/posts/calendar';
        }
    };

    /**
     * Navigate back from a post to the posts view.
     * Automatically determines the correct route based on current view (list/calendar) and public/private context.
     *
     * @param options - Navigation options
     * @param options.publicView - Whether this is a public share view
     * @param options.publicShareId - The public share ID (falls back to URL params if not provided)
     */
    const navigateBackFromPost = (options?: NavigateToPostOptions) => {
        const pathToNavigate = navigateBackFromPostPath(options);

        if (pathToNavigate) {
            navigate(pathToNavigate);
        }
    };

    return {
        navigateToPostPath,
        navigateToPost,
        navigateBackFromPostPath,
        navigateBackFromPost,
        isPostOpen: !!isPostOpen,
    };
};
